import { useState, useEffect, useCallback } from 'react';
import type { ReactNode } from 'react';
import { authApi, authEvents } from '../api';
import { disconnectSocket } from '../api/socket';
import type { User, Tokens, LoginResponse } from '../api';
import { STORAGE_KEYS } from '../constants';
import { AuthContext } from './AuthTypes';

interface AuthProviderProps {
  children: ReactNode;
}

function clearStorage() {
  localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
  localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
  localStorage.removeItem(STORAGE_KEYS.USER);
}

function getInitialUser(): User | null {
  const storedUser = localStorage.getItem(STORAGE_KEYS.USER);
  const accessToken = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);

  if (storedUser && accessToken) {
    try {
      return JSON.parse(storedUser) as User;
    } catch {
      clearStorage();
      return null;
    }
  }
  return null;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(getInitialUser);

  const saveTokens = (tokens: Tokens) => {
    localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, tokens.access.token);
    localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, tokens.refresh.token);
  };

  const login = async (data: Parameters<typeof authApi.login>[0]) => {
    const response = await authApi.login(data);
    saveTokens(response.tokens);
    localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(response.user));
    setUser(response.user);
  };

  const loginWithQrTokens = (response: LoginResponse) => {
    saveTokens(response.tokens);
    localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(response.user));
    setUser(response.user);
  };

  const logout = useCallback(async () => {
    const refreshToken = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
    if (refreshToken) {
      try {
        await authApi.logout(refreshToken);
      } catch {
        // Ignore logout API errors
      }
    }
    disconnectSocket();
    clearStorage();
    setUser(null);
  }, []);

  // Subscribe to auth events (e.g., when token refresh fails)
  useEffect(() => {
    const unsubscribe = authEvents.subscribe(() => {
      // Storage is already cleared by the API client
      disconnectSocket();
      setUser(null);
    });
    return () => {
      unsubscribe();
    };
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        login,
        loginWithQrTokens,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
